"use client";

import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from "ai";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { actionsForTool, describeAction, markUndone, type EstimatorUIMessage } from "../_logic/chat-messages";
import { estimatorToolSchemas, type EstimatorToolInput, type EstimatorToolName } from "../_logic/chat-tools";
import { compareDateWindows, suggestDestinations } from "../_logic/explore-tools";
import {
  ESTIMATOR_CHAT_API_PATH,
  MAX_TOOL_CALLS_PER_TURN,
  READ_ESTIMATE_POLL_MS,
  READ_ESTIMATE_TIMEOUT_MS,
} from "../_logic/constants";

import { toUsd } from "../_logic/estimate";
import { estimateSteps, type LiveStep } from "../_logic/live-steps";
import { resolvePlaces } from "../_logic/place-catalog";
import { rosterBlock, type Roster } from "../_logic/roster";
import type { PlaceOption } from "../_logic/places";
import { describeForModel, summarizeCheck } from "../_logic/summary";
import type { EventStart, WorkspaceAction } from "../_logic/types";
import { useEstimator } from "./EstimatorContext";

/** A chat edit as its change row shows it. */
export interface ChatChange {
  label: string;
  undo: WorkspaceAction[];
  undone: boolean;
}

interface EstimatorChatContextValue {
  messages: EstimatorUIMessage[];
  status: "submitted" | "streaming" | "ready" | "error";
  error: Error | undefined;
  /** Change rows keyed by tool call id. */
  changes: Readonly<Record<string, ChatChange>>;
  /** Sends a message; `prompt` is the full instruction behind a tapped suggestion, sent instead of the label. */
  send: (text: string, options?: { prompt?: string }) => void;
  /** Gives the assistant an attendee list for the rest of the session and asks it to set who's traveling. */
  attachRoster: (roster: Roster) => void;
  /** Sends a message and asks the layout to show the chat (rail or sheet). */
  ask: (text: string, prompt?: string) => void;
  /** Bumped by `ask`; the layout opens the chat when it changes. */
  openRequest: number;
  /** The org's active events, for grounded starting points. */
  events: EventStart[];
  /** Whether the org's teams have home cities, so "our team" can be priced. */
  hasTeamCities: boolean;
  /** Where an event's pre-filled home cities came from, for the empty assistant. */
  homeNote: string | null;
  /** Live work log per tool call. */
  steps: Readonly<Record<string, LiveStep[]>>;
  stop: () => void;
  retry: () => void;
  undo: (toolCallId: string) => void;
}

const EstimatorChatContext = createContext<EstimatorChatContextValue | null>(null);

const isToolName = (name: string): name is EstimatorToolName => name in estimatorToolSchemas;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resubmit once every tool call in the last turn has an output, unless the turn already made
 * {@link MAX_TOOL_CALLS_PER_TURN} calls: a model that keeps calling tools stops there.
 */
/** Tools that only put buttons under the reply; there is nothing for the model to read back. */
const BUTTON_ONLY_TOOLS = new Set(["tool-suggest_replies", "tool-suggest_dates", "tool-suggest_nearby_cities"]);

const shouldResubmit = ({ messages }: { messages: EstimatorUIMessage[] }): boolean => {
  if (!lastAssistantMessageIsCompleteWithToolCalls({ messages })) return false;
  const last = messages[messages.length - 1];
  if (last.parts.filter((part) => part.type.startsWith("tool-")).length >= MAX_TOOL_CALLS_PER_TURN) return false;
  // A step that only offered buttons has nothing for the model to react to; resubmitting would
  // just make it talk again under its own reply.
  const lastStepStart = last.parts.map((part) => part.type).lastIndexOf("step-start");
  const stepTools = last.parts.slice(lastStepStart + 1).filter((part) => part.type.startsWith("tool-"));
  return !stepTools.every((part) => BUTTON_ONLY_TOOLS.has(part.type));
};

/**
 * The estimator chat: streams from the estimate chat route and applies each tool call to the
 * workspace in the browser, so the chat edits the same state the table shows.
 * @param props.children - Subtree that renders the chat.
 * @param props.events - The org's active events, which `load_event` can start from.
 * @param props.hasTeamCities - Whether the org's teams have home cities.
 * @param props.initialMessages - A saved estimate's thread, to pick the conversation back up.
 * @returns The provider.
 * @throws Never.
 */
export const EstimatorChatProvider = ({
  children,
  events,
  hasTeamCities,
  homeNote = null,
  initialMessages,
}: {
  children: ReactNode;
  events: EventStart[];
  hasTeamCities: boolean;
  homeNote?: string | null;
  initialMessages?: EstimatorUIMessage[];
}) => {
  // Hooks
  const { edit, latest, flights } = useEstimator();

  // States
  const [changes, setChanges] = useState<Record<string, ChatChange>>({});
  const [steps, setSteps] = useState<Record<string, LiveStep[]>>({});
  const report = useCallback((toolCallId: string, update: (current: LiveStep[]) => LiveStep[]) => {
    setSteps((prev) => ({ ...prev, [toolCallId]: update(prev[toolCallId] ?? []) }));
  }, []);
  const [openRequest, setOpenRequest] = useState(0);
  /** Bumped by Stop, so a read_estimate still waiting knows not to answer and resubmit. */
  const stopCount = useRef(0);
  /** Edits the chat applied since the planner's last message, so its reply describes them as its own. */
  const turnApplied = useRef<string[]>([]);
  /** The attendee list attached in this session, sent with every message and never saved. */
  const roster = useRef<Roster | null>(null);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // Derived
  const transport = useMemo(
    () =>
      new DefaultChatTransport<EstimatorUIMessage>({
        api: ESTIMATOR_CHAT_API_PATH,
        body: () => {
          const { workspace, estimate, rates, eventId, costView } = latest.current;
          const applied = turnApplied.current.length
            ? `You just made these changes in this turn, and they were NOT there before: ${turnApplied.current.join("; ")}. Report them in past tense ("Added Chicago and Atlanta"), never as "already" set.\n`
            : "";
          // The planner's per-person / total switch: answer in the same terms they are looking at.
          const view =
            costView === "total"
              ? `The planner is viewing group totals (for ${estimate.headcount}); lead with totals, per person second.\n`
              : "";
          return {
            workspaceSummary: applied + view + describeForModel(workspace, estimate, rates),
            eventId,
            ...(roster.current ? { roster: rosterBlock(roster.current) } : {}),
          };
        },
      }),
    [latest]
  );

  const { messages, setMessages, sendMessage, addToolOutput, status, stop, error, regenerate } =
    useChat<EstimatorUIMessage>({
      messages: initialMessages,
      transport,
      sendAutomaticallyWhen: shouldResubmit,
      onToolCall: async ({ toolCall }) => {
        if (toolCall.dynamic || !isToolName(toolCall.toolName)) {
          // Every call needs an output, or the turn never completes.
          void addToolOutput({
            tool: toolCall.toolName as EstimatorToolName,
            toolCallId: toolCall.toolCallId,
            state: "output-error",
            errorText: `Unknown tool ${toolCall.toolName}`,
          });
          return;
        }
        const name = toolCall.toolName;
        const { toolCallId } = toolCall;
        const parsed = estimatorToolSchemas[name].safeParse(toolCall.input);
        if (!parsed.success) {
          void addToolOutput({ tool: name, toolCallId, output: { ok: false, error: parsed.error.message } });
          return;
        }

        if (name === "suggest_nearby_cities" || name === "suggest_dates" || name === "suggest_replies") {
          void addToolOutput({ tool: name, toolCallId, output: { ok: true, applied: "Shown as buttons" } });
          return;
        }

        if (name === "compare_date_windows" || name === "suggest_destinations") {
          const stopsAtStart = stopCount.current;
          const deps = {
            snapshot: () => latest.current,
            run: flights.run,
            edit: (action: WorkspaceAction) => edit(action, "chat"),
            report: (update: (steps: LiveStep[]) => LiveStep[]) => report(toolCallId, update),
          };
          const result =
            name === "compare_date_windows"
              ? await compareDateWindows(parsed.data as EstimatorToolInput<"compare_date_windows">, deps)
              : await suggestDestinations(parsed.data as EstimatorToolInput<"suggest_destinations">, deps);
          if (stopCount.current !== stopsAtStart) return;
          if (result.change) {
            const change = result.change;
            setChanges((prev) => ({ ...prev, [toolCallId]: { ...change, undone: false } }));
            turnApplied.current.push(change.label);
          }
          void addToolOutput({ tool: name, toolCallId, output: result.output });
          return;
        }

        if (name === "read_estimate") {
          const deadline = Date.now() + READ_ESTIMATE_TIMEOUT_MS;
          const stopsAtStart = stopCount.current;
          const isSettled = () =>
            latest.current.estimate.cities.every((city) => !city.flights.loading && !city.hotel.loading);
          // One tick first, so fare and hotel effects for this turn's edits have started.
          await wait(0);
          // The table's own formatting, so a step's "$4.7K" matches the cell it describes.
          const say = (usd: number) => latest.current.money.format(usd);
          const tick = () =>
            report(toolCallId, (steps) => estimateSteps(steps, latest.current.estimate, latest.current.hotels, say));
          tick();
          while (!isSettled() && Date.now() < deadline && stopCount.current === stopsAtStart) {
            await wait(READ_ESTIMATE_POLL_MS);
            tick();
          }
          tick();
          if (stopCount.current !== stopsAtStart) return;
          const { workspace, estimate, rates, hotels } = latest.current;
          void addToolOutput({
            tool: name,
            toolCallId,
            output: {
              ok: true,
              estimate: describeForModel(workspace, estimate, rates),
              checked: summarizeCheck(workspace, estimate, hotels),
            },
          });
          return;
        }

        // Cities the assistant names go through the same resolver as the pickers: a name that isn't
        // a real place is refused, and a real one is saved under its proper name and airport.
        let input = parsed.data;
        const airportPins: WorkspaceAction[] = [];
        if (name === "set_origins" || name === "add_destination") {
          const origins = name === "set_origins" ? (input as EstimatorToolInput<"set_origins">).origins : null;
          const cities = origins
            ? origins.map((origin) => origin.city)
            : [(input as EstimatorToolInput<"add_destination">).city];
          const stopsAtStart = stopCount.current;
          const places = await resolvePlaces(cities, { allowTypos: true }).catch(() => cities.map(() => null));
          if (stopCount.current !== stopsAtStart) return;
          const missing = cities.filter((_, index) => !places[index]);
          if (missing.length > 0) {
            void addToolOutput({
              tool: name,
              toolCallId,
              output: {
                ok: false,
                error: `Not a city we can find: ${missing.join(", ")}. Ask the planner which city they mean (full name, or "City, Country"); don't guess.`,
              },
            });
            return;
          }
          const resolved = places as PlaceOption[];
          input = origins
            ? { ...input, origins: origins.map((origin, index) => ({ ...origin, city: resolved[index].city })) }
            : { ...input, city: resolved[0].city };
          resolved.forEach((place) => {
            if (place.codes[0]) airportPins.push({ type: "setAirport", city: place.city, code: place.codes[0] });
          });
        }

        const translated = actionsForTool(
          name,
          input,
          (amount) => toUsd(amount, latest.current.workspace.currency, latest.current.rates),
          (currency) => currency === "USD" || Boolean(latest.current.rates[currency]),
          { workspace: latest.current.workspace, events: eventsRef.current }
        );
        if ("error" in translated) {
          void addToolOutput({ tool: name, toolCallId, output: { ok: false, error: translated.error } });
          return;
        }
        const undo: WorkspaceAction[] = [];
        const labels: string[] = [];
        for (const action of translated.actions) {
          const result = edit(action, "chat");
          if (!result.ok) {
            // All or nothing: roll back the parts of this call that did apply.
            undo.forEach((inverse) => edit(inverse, "user"));
            void addToolOutput({ tool: name, toolCallId, output: { ok: false, error: result.error } });
            return;
          }
          undo.unshift(result.undo);
          labels.push(describeAction(action, latest.current.workspace));
        }
        for (const pin of airportPins) {
          const result = edit(pin, "chat");
          if (result.ok) undo.unshift(result.undo);
        }
        const label =
          labels.map((text, index) => (index === 0 ? text : text.charAt(0).toLowerCase() + text.slice(1))).join(", ") ||
          "No change";
        setChanges((prev) => ({ ...prev, [toolCallId]: { label, undo, undone: false } }));
        turnApplied.current.push(label);
        void addToolOutput({ tool: name, toolCallId, output: { ok: true, applied: label } });
      },
    });

  // Handlers
  const send = useCallback(
    (text: string, options: { prompt?: string } = {}) => {
      turnApplied.current = [];
      void sendMessage({ text, metadata: options.prompt ? { prompt: options.prompt } : undefined });
    },
    [sendMessage]
  );

  const attachRoster = useCallback(
    (next: Roster) => {
      roster.current = next;
      turnApplied.current = [];
      void sendMessage({
        text: "Set who's traveling from this list",
        metadata: {
          attachment: { fileName: next.fileName, ...(next.people === undefined ? {} : { people: next.people }) },
          prompt: `I attached ${next.fileName}; it's in the roster block. Set who's traveling from it.`,
        },
      });
    },
    [sendMessage]
  );

  const undo = useCallback(
    (toolCallId: string) => {
      const change = changes[toolCallId];
      if (!change || change.undone) return;
      change.undo.forEach((action) => edit(action, "user"));
      setChanges((prev) => ({ ...prev, [toolCallId]: { ...change, undone: true } }));
      // Marked on the tool's output too, so the saved thread reopens with it undone and the model knows.
      setMessages((current) => markUndone(current, toolCallId));
    },
    [changes, edit, setMessages]
  );

  const retry = useCallback(() => void regenerate(), [regenerate]);

  const ask = useCallback(
    (text: string, prompt?: string) => {
      setOpenRequest((count) => count + 1);
      turnApplied.current = [];
      void sendMessage({ text, metadata: prompt ? { prompt } : undefined });
    },
    [sendMessage]
  );

  // Render
  const value = useMemo<EstimatorChatContextValue>(
    () => ({
      messages,
      status,
      error,
      changes,
      send,
      attachRoster,
      ask,
      openRequest,
      events,
      hasTeamCities,
      homeNote,
      steps,
      stop: () => {
        stopCount.current += 1;
        void stop();
      },
      retry,
      undo,
    }),
    [
      messages,
      status,
      error,
      changes,
      send,
      attachRoster,
      ask,
      openRequest,
      events,
      hasTeamCities,
      homeNote,
      steps,
      stop,
      retry,
      undo,
    ]
  );
  return <EstimatorChatContext.Provider value={value}>{children}</EstimatorChatContext.Provider>;
};

/**
 * The estimator chat context.
 * @returns The context value.
 * @throws When used outside {@link EstimatorChatProvider}.
 */
export function useEstimatorChat(): EstimatorChatContextValue {
  const value = useContext(EstimatorChatContext);
  if (!value) throw new Error("useEstimatorChat must be used inside EstimatorChatProvider");
  return value;
}
